import { useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { db } from "../firebase/config";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

export default function ContactSalesModal({
  open,
  onClose,
  source = "homepage",
}) {
  const initial = {
    fullName: "",
    email: "",
    company: "",
    message: "",
    consent: false,
    // honeypot
    website: "",
  };

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const nameRef = useRef(null);

  // Lock scroll + Esc to close
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);

  // Autofocus first field when opened
  useEffect(() => {
    if (open && nameRef.current && !done) nameRef.current.focus();
  }, [open, done]);

  // Reset transient state when modal is reopened
  useEffect(() => {
    if (open) {
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const onChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.fullName.trim()) return "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid work email.";
    if (!form.message.trim()) return "Tell us briefly how we can help.";
    if (!form.consent) return "Please consent to be contacted.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return; // honeypot bot guard
    const v = validate();
    if (v) return setError(v);

    setError("");
    setSubmitting(true);
    try {
      await addDoc(collection(db, "sales_request"), {
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        company: form.company.trim(),
        message: form.message.trim(),
        status: "new",
        source,
        createdAt: serverTimestamp(),
      });

      setDone(true);
      setForm(initial);
    } catch (err) {
      console.error(err);
      setError("Could not submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="contact-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-title"
      aria-describedby="contact-subtitle"
      onClick={onClose}
    >
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-header">
          <div>
            <h3 id="contact-title">Request Demo</h3>
            <p id="contact-subtitle" className="contact-subtitle">
              We usually reply within one business day.
            </p>
          </div>
          <button
            className="contact-close"
            aria-label="Close"
            onClick={onClose}
            type="button"
          >
            <IoMdClose />
          </button>
        </div>

        {done ? (
          <div className="contact-success">
            <h4>Thanks!</h4>
            <p>We’ll get back to you within one business day.</p>
            <button className="contact-submit" onClick={onClose} type="button">
              Close
            </button>
          </div>
        ) : (
          <form className="contact-form" onSubmit={onSubmit} noValidate>
            {error && (
              <div className="contact-error" role="alert" aria-live="assertive">
                {error}
              </div>
            )}

            {/* Honeypot */}

            {/* Two-column on md+ */}
            <div className="contact-grid">
              <label>
                Full name*
                <input
                  ref={nameRef}
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  placeholder="Jane Doe"
                  required
                />
              </label>
              <label>
                Work email*
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@company.com"
                  required
                />
              </label>
            </div>

            <label>
              Company
              <input
                name="company"
                value={form.company}
                onChange={onChange}
                placeholder="Company Inc."
              />
            </label>

            <label>
              How can we help?*
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={onChange}
                placeholder="e.g., rollout questions, procurement, data security…"
                required
              />
            </label>

            <label className="consent">
              <input
                type="checkbox"
                name="consent"
                checked={form.consent}
                onChange={onChange}
              />
              I agree to be contacted about Oiiko.
            </label>

            <div className="contact-actions">
              <button
                className={`contact-submit ${submitting ? "is-loading" : ""}`}
                disabled={submitting}
                type="submit"
              >
                {submitting ? "Sending…" : "Send"}
              </button>
            </div>

            <p className="contact-footnote">
              By submitting, you agree to our{" "}
              <a href="/privacy">Privacy Notice</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
